import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

/**
 * 바알시불 감정 타입
 *
 * 기획서 기반:
 * - idle: 기본 상태
 * - happy: 기분 좋음 (레벨업, 목표 달성)
 * - cheering: 응원 중 (운동 시작, 격려)
 * - celebrating: 축하 (업적 해금, 퀘스트 완료)
 * - proud: 자랑스러움 (신기록, 연속 출석)
 * - tired: 지침 (오랜 운동 후)
 * - sad: 슬픔 (에러, 실패)
 * - angry: 화남 (퀘스트 미완료, 오랜 미접속)
 * - workout: 운동 중
 * - resting: 쉬는 중
 * - sleeping: 자는 중 (야간)
 */
export type BaralMood =
  | 'idle'
  | 'happy'
  | 'cheering'
  | 'celebrating'
  | 'proud'
  | 'tired'
  | 'sad'
  | 'angry'
  | 'workout'
  | 'resting'
  | 'sleeping';

// 상황별 메시지
const MOOD_MESSAGES: Record<BaralMood, string[]> = {
  idle: [
    '오늘도 화이팅!',
    '뭐하고 있어?',
    '운동하러 갈까?',
    '심심해...',
  ],
  happy: [
    '기분 최고야!',
    '잘했어!',
    '역시 대단해!',
    '이 조자세 봐~',
  ],
  cheering: [
    '할 수 있어!',
    '파이팅!',
    '포기하지마!',
    '거의 다 왔어!',
  ],
  celebrating: [
    '축하해!!!',
    '대박! 해냈다!',
    '너무 멋져!',
    '짝짝짝!',
  ],
  proud: [
    '역시 내 주인!',
    '자랑스러워!',
    '최고야!',
    '기록 갱신!',
  ],
  tired: [
    '휴... 힘들다',
    '잠깐 쉬자...',
    '오늘 많이 했네',
    '푹 쉬어!',
  ],
  sad: [
    '괜찮아?',
    '힘내...',
    '다음엔 잘 될 거야',
    '포기하지마...',
  ],
  angry: [
    '운동 안 해?!',
    '게으름뱅이!',
    '당장 일어나!',
    '화났어!',
  ],
  workout: [
    '좋아! 운동 중!',
    '더! 더!',
    '집중!',
    '자세 똑바로!',
  ],
  resting: [
    '잘 쉬어~',
    '휴식도 운동이야',
    '수분 보충!',
    '스트레칭 했어?',
  ],
  sleeping: [
    'zzZ...',
    '꿈 속에서도 운동...',
    '내일 보자~',
    '굿나잇!',
  ],
};

interface BaralContextType {
  mood: BaralMood;
  message: string;
  setMood: (mood: BaralMood, customMessage?: string) => void;
  triggerCelebration: (customMessage?: string) => void;
  triggerLevelUp: () => void;
  triggerWorkoutStart: () => void;
  triggerWorkoutEnd: () => void;
  triggerAchievement: (achievementName: string) => void;
  triggerQuestComplete: (questName: string) => void;
  triggerError: () => void;
  resetMood: () => void;
}

const BaralContext = createContext<BaralContextType | undefined>(undefined);

interface BaralProviderProps {
  children: ReactNode;
}

export function BaralProvider({ children }: BaralProviderProps) {
  const [mood, setMoodState] = useState<BaralMood>('idle');
  const [message, setMessage] = useState<string>(MOOD_MESSAGES.idle[0]);
  const [moodTimeoutId, setMoodTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // 랜덤 메시지 선택
  const getRandomMessage = (moodType: BaralMood): string => {
    const messages = MOOD_MESSAGES[moodType];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // 시간대에 따른 기본 mood 결정
  const getTimeBasedMood = (): BaralMood => {
    const hour = new Date().getHours();
    if (hour >= 23 || hour < 6) return 'sleeping';
    return 'idle';
  };

  // mood 변경 (일정 시간 후 자동 복귀)
  const setMood = useCallback((newMood: BaralMood, customMessage?: string, duration: number = 5000) => {
    // 기존 타이머 클리어
    if (moodTimeoutId) {
      clearTimeout(moodTimeoutId);
    }

    setMoodState(newMood);
    setMessage(customMessage || getRandomMessage(newMood));

    // 특별한 mood는 일정 시간 후 idle로 복귀
    if (['celebrating', 'cheering', 'happy', 'proud', 'sad', 'angry'].includes(newMood)) {
      const timeoutId = setTimeout(() => {
        setMoodState(getTimeBasedMood());
        setMessage(getRandomMessage(getTimeBasedMood()));
      }, duration);
      setMoodTimeoutId(timeoutId);
    }
  }, [moodTimeoutId]);

  // 축하 트리거 (레벨업, 업적 등)
  const triggerCelebration = useCallback((customMessage?: string) => {
    setMood('celebrating', customMessage || '축하해!!! 대단해!!!', 6000);
  }, [setMood]);

  // 레벨업 트리거
  const triggerLevelUp = useCallback(() => {
    setMood('celebrating', '레벨 업!!! 🎉', 8000);
  }, [setMood]);

  // 운동 시작 트리거
  const triggerWorkoutStart = useCallback(() => {
    setMood('workout', '좋아! 운동 시작!');
  }, [setMood]);

  // 운동 종료 트리거
  const triggerWorkoutEnd = useCallback(() => {
    setMood('tired', '오늘도 수고했어!', 10000);
  }, [setMood]);

  // 업적 달성 트리거
  const triggerAchievement = useCallback((achievementName: string) => {
    setMood('proud', `업적 달성! "${achievementName}"`, 7000);
  }, [setMood]);

  // 퀘스트 완료 트리거
  const triggerQuestComplete = useCallback((questName: string) => {
    setMood('celebrating', `퀘스트 완료! "${questName}"`, 6000);
  }, [setMood]);

  // 에러 트리거
  const triggerError = useCallback(() => {
    setMood('sad', '앗, 뭔가 잘못됐어...', 4000);
  }, [setMood]);

  // 기본 상태로 리셋
  const resetMood = useCallback(() => {
    if (moodTimeoutId) {
      clearTimeout(moodTimeoutId);
    }
    const baseMood = getTimeBasedMood();
    setMoodState(baseMood);
    setMessage(getRandomMessage(baseMood));
  }, [moodTimeoutId]);

  // 초기 로드 시 시간대 기반 mood 설정
  useEffect(() => {
    const baseMood = getTimeBasedMood();
    setMoodState(baseMood);
    setMessage(getRandomMessage(baseMood));

    // 정기적으로 idle 메시지 변경 (30초마다)
    const interval = setInterval(() => {
      if (mood === 'idle' || mood === 'sleeping') {
        setMessage(getRandomMessage(mood));
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      if (moodTimeoutId) clearTimeout(moodTimeoutId);
    };
  }, []);

  return (
    <BaralContext.Provider
      value={{
        mood,
        message,
        setMood,
        triggerCelebration,
        triggerLevelUp,
        triggerWorkoutStart,
        triggerWorkoutEnd,
        triggerAchievement,
        triggerQuestComplete,
        triggerError,
        resetMood,
      }}
    >
      {children}
    </BaralContext.Provider>
  );
}

export function useBaral() {
  const context = useContext(BaralContext);
  if (context === undefined) {
    throw new Error('useBaral must be used within a BaralProvider');
  }
  return context;
}
